import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { InspectorHeader } from '../InspectorHeader/InspectorHeader';
import IconCheckmarkSquare from '@/assets/icons/icon-checkmark-square.svg?react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNode } from '@craftjs/core';
import { TextField } from '@/components/inspector-fields/TextField/TextField';
import { LayoutElementV2 } from '@/components/inspector-fields/LayoutElement/LayoutElementV2';
import NodeColorField from '@/components/inspector-fields/ColorField/NodeColorField';
import NodeStrokeSettings from '@/components/inspector-fields/SliderField/NodeStrokeSettings';
import NodeShadowField from '@/components/inspector-fields/ShadowField/NodeShadowField';
import { InspectorPopupTrigger } from './InspectorPopupTrigger';

interface InspectorPopupProps {
  className: string;
  text: string;
}

export const InspectorPopup: React.FC<InspectorPopupProps> = ({ className, text = 'Popup' }) => {
  const {
    actions: { setCustom },
    customDisplayName,
  } = useNode((node) => ({
    customDisplayName: (node.data.custom?.displayName || '') as string,
  }));

  const [popupName, setPopupName] = useState(customDisplayName);

  useEffect(() => {
    setPopupName(customDisplayName);
  }, [customDisplayName]);

  const handlePopupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPopupName(value);
    setCustom((custom: Record<string, unknown>) => {
      custom.displayName = value;
    }, 500);
  };

  return (
    <div
      className={clsx(
        'inspector bg-neutral-0 relative z-10 inline-flex h-[calc(100vh-52px)] max-w-[240px] flex-col border-l border-neutral-100',
        className,
      )}
    >
      <InspectorHeader
        className="max-w-[240px] text-neutral-900"
        icon={IconCheckmarkSquare}
        text={text}
      />

      <ScrollArea className="h-[calc(100vh-104px)] overflow-hidden">
        <div className="flex flex-col gap-3 p-3">
          <LayoutElementV2 className="max-w-[216px]" showMargin={false} showPosition={false} />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <TextField
            className="max-w-[216px]"
            inputValue={popupName}
            onChange={handlePopupNameChange}
            placeholderText="Popup name"
            state="default"
            text="Name"
            type="text"
          />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <InspectorPopupTrigger />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Appearance</p>

          <NodeColorField propertyName="popupColor" label="Popup Color" />

          <NodeColorField propertyName="overlayColor" label="Overlay Color" />

          <NodeColorField
            propertyName="borderColor"
            ensureStrokeVisibleProp="borderWidth"
            label="Stroke Color"
          />

          <NodeStrokeSettings />

          <NodeShadowField />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
        </div>
      </ScrollArea>
    </div>
  );
};
